import { RefreshFlag } from "@/refresh_flag.tsx"
import { useLocation } from "wouter-preact"
import { useFetch } from "@/utils"
import { api_fetch, data_or_throw, DeductionDetailJson } from "@/api"
import { CenterSpinner } from "@/app/loading.tsx"
import { DeductionDetailContent } from "./DeductionDetailContent.tsx"
import {
  DeductionDetailModalId,
  DeductionDetailModalOpen,
  closeDeductionDetailModal,
} from "@/deduction/deduction_detail_modal_state.tsx"
import { should_show_detail_spinner } from "@/utils/detail_refresh.ts"
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog.tsx"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, ExternalLink, Calculator, Link } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { accounting_pages_hidden } from "@/app/page_access"
import { UserState } from "@/auth/user"
import { useOrgSettings } from "@/org_settings/state"

/** Popovers/portals (e.g. promotion picker) render outside dialog content; don't close the modal when using them. */
function handle_deduction_detail_modal_interact_outside(e: Event) {
  const target = e.target as HTMLElement
  if (
    target?.closest?.("[data-radix-popper-content-wrapper]") ||
    target?.closest?.("[data-radix-select-content]") ||
    target?.closest?.("[data-radix-dropdown-menu-content]") ||
    target?.closest?.("[role='listbox']") ||
    target?.closest?.("[cmdk-list]") ||
    target?.closest?.("[cmdk-input]") ||
    target?.closest?.("[cmdk-item]")
  ) {
    e.preventDefault()
  }
}

export function DeductionDetailModal() {
  const deductionId = DeductionDetailModalId.use((id: string | null) => id)
  const isOpen = DeductionDetailModalOpen.use((open: boolean) => open)

  if (!deductionId) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && closeDeductionDetailModal()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          class="fixed left-[50%] top-4 z-50 w-[calc(100%-2rem)] max-w-full translate-x-[-50%] h-[calc(100vh-2rem)] border bg-background shadow-lg sm:rounded-lg flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onInteractOutside={handle_deduction_detail_modal_interact_outside}
          onPointerDownOutside={handle_deduction_detail_modal_interact_outside}
        >
          <DialogTitle className="sr-only">Deduction details</DialogTitle>
          <DeductionDetailModalContent deductionId={deductionId} />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

interface DeductionDetailModalContentProps {
  deductionId: string
}

function DeductionDetailModalContent({ deductionId }: DeductionDetailModalContentProps) {
  const statusFlag = RefreshFlag.use((sf: boolean) => sf!)
  const [, setLocation] = useLocation()

  const {
    data: deduction,
    loading,
    setData: setDeduction,
  } = useFetch(async () => {
    const res = await api_fetch<DeductionDetailJson>(`/deduction/${deductionId}`)
    return data_or_throw(res)
  }, [deductionId, statusFlag])

  const hide_accounting = accounting_pages_hidden(UserState.use(), useOrgSettings())

  function handleViewDeduction() {
    closeDeductionDetailModal()
    setLocation(`/deduction/${deductionId}`)
  }

  function handleViewAccounting() {
    closeDeductionDetailModal()
    setLocation(`/accounting/transaction/${deductionId}`)
  }

  function handleCopyUrl() {
    const url = `${window.location.origin}/deduction/${deductionId}`
    // The browser refuses the write when the document is not focused (FRONTEND-6C).
    navigator.clipboard.writeText(url).then(
      () => toast({ title: "Link copied", description: "Deduction URL copied to clipboard" }),
      () => toast({ title: "Could not copy the link", description: url, variant: "destructive" }),
    )
  }

  return (
    <>
      {/* Header */}
      <div class="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
        <DialogPrimitive.Close class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <X className="h-5 w-5" />
          <span class="sr-only">Close</span>
        </DialogPrimitive.Close>
        <div class="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyUrl}>
            <Link className="h-5 w-5" />
            Copy Link
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewDeduction}>
            <ExternalLink className="h-5 w-5" />
            View Deduction
          </Button>
          {!hide_accounting && (
            <Button variant="outline" size="sm" onClick={handleViewAccounting}>
              <Calculator className="h-5 w-5" />
              View Accounting
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div class="flex-1 overflow-auto">
        {/* Keep content mounted during background refetches so unsaved edits survive */}
        {!deduction || should_show_detail_spinner(loading, deduction.id, deductionId) ? (
          <CenterSpinner />
        ) : (
          <DeductionDetailContent deduction={deduction} onUpdate={setDeduction} />
        )}
      </div>
    </>
  )
}
